import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'kt_theme_mode_value';
const MENU_STORAGE_KEY = 'kt_theme_mode_menu';
const DEFAULT_MODE: ThemeMode = 'light';

@Injectable({ providedIn: 'root' })
export class ThemeModeService {
  private readonly modeSubject = new BehaviorSubject<ThemeMode>(this.readStoredMode());
  private readonly resolvedSubject = new BehaviorSubject<ResolvedThemeMode>(this.resolve(this.modeSubject.value));
  private readonly mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  constructor() {
    this.applyMode(this.modeSubject.value);
    this.mediaQuery.addEventListener('change', () => {
      if (this.modeSubject.value === 'system') {
        this.applyMode('system');
      }
    });
  }

  get mode$(): Observable<ThemeMode> {
    return this.modeSubject.asObservable();
  }

  get resolved$(): Observable<ResolvedThemeMode> {
    return this.resolvedSubject.asObservable();
  }

  getMode(): ThemeMode {
    return this.modeSubject.value;
  }

  getResolvedMode(): ResolvedThemeMode {
    return this.resolvedSubject.value;
  }

  setMode(mode: ThemeMode): void {
    this.persistMode(mode);
    this.applyMode(mode);
    this.modeSubject.next(mode);
  }

  toggle(): void {
    const next: ThemeMode = this.getResolvedMode() === 'dark' ? 'light' : 'dark';
    this.setMode(next);
  }

  private applyMode(mode: ThemeMode): void {
    const resolved = this.resolve(mode);
    const root = document.documentElement;
    root.setAttribute('data-kt-theme-mode-switching', 'true');
    root.setAttribute('data-bs-theme', resolved);
    // Roomgrid reads [data-theme]; Bootstrap/Metronic read [data-bs-theme]. Keep both in step.
    root.setAttribute('data-theme', resolved);
    setTimeout(() => root.removeAttribute('data-kt-theme-mode-switching'), 300);
    this.resolvedSubject.next(resolved);
  }

  private resolve(mode: ThemeMode): ResolvedThemeMode {
    if (mode === 'system') {
      return this.mediaQuery.matches ? 'dark' : 'light';
    }
    return mode;
  }

  private readStoredMode(): ThemeMode {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    } catch {
      // localStorage may be unavailable (e.g., privacy mode)
    }
    return DEFAULT_MODE;
  }

  private persistMode(mode: ThemeMode): void {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
      localStorage.setItem(MENU_STORAGE_KEY, mode);
    } catch {
      // ignore persistence failures
    }
  }
}
